import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { formatNumber } from "../utils/formatters"

const INITIAL_ITEM_FILTERS = {
  pageType: "",
  make: "",
  model: "",
  type: "",
  year: "",
  condition: "",
}

export default function GA4PropertyCCR() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [ccrResults, setCcrResults] = useState([])
  const [ccrLoading, setCcrLoading] = useState(false)
  const [ccrError, setCcrError] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [availableMonths, setAvailableMonths] = useState([])
  const [monthFilter, setMonthFilter] = useState("")
  const [viewMode, setViewMode] = useState("standard")
  const [itemFilters, setItemFilters] = useState({ ...INITIAL_ITEM_FILTERS })
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setCcrResults([])
      setAvailableMonths([])
      setCcrError(propertyError || null)
      setCcrLoading(false)
      return
    }

    let cancelled = false

    const fetchData = async () => {
      setCcrLoading(true)
      setCcrError(null)
      try {
        const monthSet = new Set()
        const { data: rows, error: rowsError } = await supabase
          .from("ga4_ccr_results")
          .select(
            "event_owner, product_name, event_action, element_text, event_count, monthly_breakdown, item_breakdown"
          )
          .eq("ga4_property_id", propInfo.id)
          .order("event_owner", { ascending: true })
          .order("product_name", { ascending: true })
          .order("event_action", { ascending: true })
          .order("element_text", { ascending: true })

        if (rowsError) throw rowsError
        if (cancelled) return

        setCcrResults(
          (rows || []).map((row) => {
            const event_owner = normalizeText(row.event_owner)
            const product_name = normalizeText(row.product_name)
            const event_action = normalizeText(row.event_action)
            const element_text = normalizeText(row.element_text)
            const { counts: monthlyEventCounts } =
              extractMonthlyCounts(row?.monthly_breakdown, monthSet)

            const itemBreakdown = Array.isArray(row?.item_breakdown)
              ? row.item_breakdown
                  .map((entry) =>
                    sanitizeItemBreakdownEntry(entry, element_text)
                  )
                  .filter(Boolean)
              : []
            const { ctaCount: itemCtaCount, submissionCount: itemSubmissionCount } =
              getItemBreakdownTotals(itemBreakdown)
            const effectiveEventCount = itemCtaCount
            const submission_count = itemSubmissionCount
            const submission_rate =
              effectiveEventCount > 0 ? submission_count / effectiveEventCount : null

            return {
              event_owner,
              product_name,
              event_action,
              element_text,
              event_count: effectiveEventCount,
              submission_count,
              submission_rate,
              itemCtaCount,
              itemSubmissionCount,
              monthlyEventCounts,
              monthlySubmissionCounts: {},
              itemBreakdown,
              rowKey: createRowKey(event_owner, product_name, event_action, element_text),
            }
          })
        )
        setAvailableMonths(Array.from(monthSet).sort())
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 CCR data", err)
        setCcrResults([])
        setAvailableMonths([])
        setCcrError(err)
      } finally {
        if (!cancelled) setCcrLoading(false)
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const loading = propertyLoading || ccrLoading

  useEffect(() => {
    if (monthFilter && !availableMonths.includes(monthFilter)) {
      setMonthFilter("")
    }
  }, [availableMonths, monthFilter])

  useEffect(() => {
    if (viewMode === "item" && monthFilter) {
      setMonthFilter("")
    }
  }, [viewMode, monthFilter])

  const computedRows = useMemo(() => {
    return ccrResults
      .map((row) => {
        const eventCount = monthFilter
          ? row.monthlyEventCounts?.[monthFilter] ?? 0
          : row.event_count
        const submissionCount = monthFilter
          ? row.monthlySubmissionCounts?.[monthFilter] ?? 0
          : row.itemSubmissionCount
        const submissionRate =
          eventCount > 0 ? submissionCount / eventCount : null
        return {
          ...row,
          event_count: eventCount,
          submission_count: submissionCount,
          submission_rate: submissionRate,
        }
      })
      .filter((row) => {
        if (!monthFilter) return true
        return row.event_count > 0 || row.submission_count > 0
      })
  }, [ccrResults, monthFilter])

  useEffect(() => {
    setSelectedRowKeys((prev) =>
      prev.filter((key) => computedRows.some((row) => row.rowKey === key))
    )
  }, [computedRows])

  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys])

  const selectedRows = useMemo(
    () => computedRows.filter((row) => selectedRowKeySet.has(row.rowKey)),
    [computedRows, selectedRowKeySet]
  )

  const remainingRows = useMemo(
    () => computedRows.filter((row) => !selectedRowKeySet.has(row.rowKey)),
    [computedRows, selectedRowKeySet]
  )

  const monthOptions = useMemo(() => {
    return availableMonths.map((month) => ({
      value: month,
      label: formatYearMonth(month),
    }))
  }, [availableMonths])

  const itemFilterOptions = useMemo(() => {
    const pageTypes = new Set()
    const makes = new Set()
    const models = new Set()
    const types = new Set()
    const years = new Set()
    const conditions = new Set()

    const addValue = (set, value) => {
      if (!value || typeof value !== "string") return
      const trimmed = value.trim()
      if (!trimmed) return
      set.add(trimmed)
    }

    for (const row of ccrResults) {
      const breakdown = Array.isArray(row?.itemBreakdown) ? row.itemBreakdown : []
      for (const entry of breakdown) {
        addValue(pageTypes, entry?.page_type)
        addValue(makes, entry?.item_make)
        addValue(models, entry?.item_model)
        addValue(types, entry?.item_type)
        addValue(years, entry?.item_year)
        addValue(conditions, entry?.item_condition)
      }
    }

    const sortValues = (set) =>
      Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))

    return {
      pageTypes: sortValues(pageTypes),
      makes: sortValues(makes),
      models: sortValues(models),
      types: sortValues(types),
      years: sortValues(years),
      conditions: sortValues(conditions),
    }
  }, [ccrResults])

  const itemViewData = useMemo(() => {
    const vinRows = []
    const otherRows = []
    const pageTypeSummary = new Map()
    const groupedRows = new Map()

    const filters = itemFilters

    for (const row of ccrResults) {
      const breakdown = Array.isArray(row?.itemBreakdown) ? row.itemBreakdown : []
      for (const entry of breakdown) {
        if (!matchesItemFilters(entry, filters)) continue
        const eventCount = normalizeEventCountValue(entry?.asc_cta_count)
        const submissionCount = normalizeEventCountValue(
          entry?.asc_form_submission_count
        )
        if (!eventCount || !submissionCount) continue
        const submissionRate =
          eventCount > 0 ? submissionCount / eventCount : null
        const normalizedRow = {
          key: "",
          event_owner: row.event_owner,
          product_name: row.product_name,
          event_action: row.event_action,
          element_text: entry.element_text || "(not set)",
          page_type: entry.page_type || "",
          item_id: entry.item_id || "",
          item_make: entry.item_make || "",
          item_model: entry.item_model || "",
          item_type: entry.item_type || "",
          item_year: entry.item_year || "",
          item_condition: entry.item_condition || "",
          event_count: eventCount,
          asc_cta_count: normalizeEventCountValue(entry?.asc_cta_count),
          asc_form_submission_count: normalizeEventCountValue(
            entry?.asc_form_submission_count
          ),
          submission_count: submissionCount,
          submission_rate: submissionRate,
          is_vin_match: Boolean(entry.isVinMatch),
          matched_vins: new Set(entry.item_id ? [entry.item_id] : []),
        }

        const groupedKey = [
          row.rowKey,
          normalizedRow.page_type || "(not set)",
          normalizedRow.item_make || "(not set)",
          normalizedRow.item_model || "(not set)",
          normalizedRow.item_type || "(not set)",
          normalizedRow.item_year || "(not set)",
          normalizedRow.item_condition || "(not set)",
          normalizedRow.is_vin_match ? "1" : "0",
        ].join("|||")
        const existing = groupedRows.get(groupedKey)
        if (!existing) {
          groupedRows.set(groupedKey, normalizedRow)
        } else {
          existing.event_count += normalizedRow.event_count
          existing.asc_cta_count += normalizedRow.asc_cta_count
          existing.asc_form_submission_count += normalizedRow.asc_form_submission_count
          existing.submission_count += normalizedRow.submission_count
          if (normalizedRow.item_id) existing.matched_vins.add(normalizedRow.item_id)
        }
      }
    }

    for (const [groupedKey, groupedRow] of groupedRows.entries()) {
      groupedRow.key = groupedKey
      groupedRow.item_id = Array.from(groupedRow.matched_vins).sort()[0] || ""
      groupedRow.matched_vin_count = groupedRow.matched_vins.size
      groupedRow.submission_rate =
        groupedRow.event_count > 0
          ? groupedRow.submission_count / groupedRow.event_count
          : null

      if (groupedRow.is_vin_match) {
        vinRows.push(groupedRow)
      } else if (groupedRow.matched_vin_count > 0) {
        otherRows.push(groupedRow)
        const summaryKey = groupedRow.page_type || "(not set)"
        const summary =
          pageTypeSummary.get(summaryKey) || {
            page_type: summaryKey,
            total_event_count: 0,
            unique_vins: new Set(),
            row_count: 0,
          }
        summary.total_event_count += groupedRow.event_count
        for (const vin of groupedRow.matched_vins) {
          summary.unique_vins.add(vin)
        }
        summary.row_count += 1
        pageTypeSummary.set(summaryKey, summary)
      }
    }

    vinRows.sort((a, b) => {
      if (b.event_count !== a.event_count) return b.event_count - a.event_count
      if (a.event_owner !== b.event_owner)
        return a.event_owner.localeCompare(b.event_owner)
      if (a.product_name !== b.product_name)
        return a.product_name.localeCompare(b.product_name)
      if (a.event_action !== b.event_action)
        return a.event_action.localeCompare(b.event_action)
      return a.element_text.localeCompare(b.element_text)
    })

    otherRows.sort((a, b) => {
      if ((a.page_type || "") !== (b.page_type || ""))
        return (a.page_type || "").localeCompare(b.page_type || "")
      if (b.event_count !== a.event_count) return b.event_count - a.event_count
      return a.element_text.localeCompare(b.element_text)
    })

    const pageTypeRows = Array.from(pageTypeSummary.values())
      .map((entry) => ({
        page_type: entry.page_type,
        total_event_count: entry.total_event_count,
        unique_vins: entry.unique_vins.size,
        row_count: entry.row_count,
      }))
      .sort((a, b) => {
        if (b.total_event_count !== a.total_event_count)
          return b.total_event_count - a.total_event_count
        return a.page_type.localeCompare(b.page_type)
      })

    return { vinRows, otherRows, pageTypeRows }
  }, [ccrResults, itemFilters])

  const handleItemFilterChange = (key) => (event) => {
    setItemFilters((prev) => ({ ...prev, [key]: event.target.value }))
  }

  const resetItemFilters = () => {
    setItemFilters({ ...INITIAL_ITEM_FILTERS })
  }

  const formattedSelectedMonth = monthFilter
    ? formatYearMonth(monthFilter) || monthFilter
    : ""
  const monthEmptyMessage = monthFilter
    ? `No CCR interaction results saved for ${formattedSelectedMonth}`
    : "No CCR interaction results saved"

  const allCtasEmptyMessage =
    remainingRows.length === 0 && computedRows.length > 0
      ? "All CTAs are currently selected. Uncheck a row above to return it here."
      : monthEmptyMessage

  const toggleRowSelection = (rowKey) => {
    setSelectedRowKeys((prev) => {
      if (prev.includes(rowKey)) {
        return prev.filter((key) => key !== rowKey)
      }
      return [...prev, rowKey]
    })
  }

  return (
    <div className="page-container">
      <h2>CTA Completion Rate (CCR)</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : ccrError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load CCR data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              flexWrap: "wrap",
              gap: "1.5rem",
              alignItems: "flex-end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ marginBottom: "0.25rem" }}>View mode:</span>
              <select
                id="ccr-view-mode"
                value={viewMode}
                onChange={(event) => setViewMode(event.target.value)}
              >
                <option value="standard">All CTAs</option>
                <option value="item">VIN &amp; inventory view</option>
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ marginBottom: "0.25rem" }}>Month filter:</span>
              <select
                id="ccr-month-filter"
                value={monthFilter}
                onChange={(event) => setMonthFilter(event.target.value)}
                disabled={viewMode === "item"}
              >
                <option value="">All months</option>
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {viewMode === "item" && (
                <span style={{ marginTop: "0.25rem", fontSize: "0.85em", color: "#555" }}>
                  Month filtering is unavailable in VIN view.
                </span>
              )}
            </label>
          </div>
          {viewMode === "item" && (
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                alignItems: "flex-end",
              }}
            >
              {renderItemFilterControl(
                "pageType",
                "Page type",
                itemFilters.pageType,
                itemFilterOptions.pageTypes,
                handleItemFilterChange("pageType")
              )}
              {renderItemFilterControl(
                "make",
                "Item make",
                itemFilters.make,
                itemFilterOptions.makes,
                handleItemFilterChange("make")
              )}
              {renderItemFilterControl(
                "model",
                "Item model",
                itemFilters.model,
                itemFilterOptions.models,
                handleItemFilterChange("model")
              )}
              {renderItemFilterControl(
                "type",
                "Item type",
                itemFilters.type,
                itemFilterOptions.types,
                handleItemFilterChange("type")
              )}
              {renderItemFilterControl(
                "year",
                "Item year",
                itemFilters.year,
                itemFilterOptions.years,
                handleItemFilterChange("year")
              )}
              {renderItemFilterControl(
                "condition",
                "Item condition",
                itemFilters.condition,
                itemFilterOptions.conditions,
                handleItemFilterChange("condition")
              )}
              <button type="button" onClick={resetItemFilters}>
                Reset filters
              </button>
            </div>
          )}
          {viewMode === "standard" ? (
            computedRows.length ? (
              <>
                {renderTable(
                  "Filtered CTAs",
                  selectedRows,
                  selectedRowKeySet,
                  toggleRowSelection,
                  "No CTAs are currently selected. Check a row below to view it here."
                )}
                {renderTable(
                  "All CTAs",
                  remainingRows,
                  selectedRowKeySet,
                  toggleRowSelection,
                  allCtasEmptyMessage
                )}
              </>
            ) : (
              <p style={{ marginTop: "1rem" }}>{monthEmptyMessage}</p>
            )
          ) : (
            renderItemView(itemViewData)
          )}
        </>
      )}
    </div>
  )
}

function normalizeText(value) {
  if (value === null || value === undefined || value === "") return "(not set)"
  if (typeof value !== "string") return String(value)
  return value
}

function formatPercentValue(value) {
  if (value === null || value === undefined) return "—"
  return new Intl.NumberFormat(undefined, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value)
}

function createRowKey(event_owner, product_name, event_action, element_text) {
  return [event_owner, product_name, event_action, element_text || "—"].join("|||")
}

function normalizeKeyValue(value) {
  if (value === null || value === undefined || value === "") return "(not set)".toLowerCase()
  if (typeof value !== "string") return String(value).toLowerCase()
  return value.trim().toLowerCase()
}

function getMonthKey(value) {
  if (!value || typeof value !== "object") return ""
  const keys = ["yearMonth", "year_month", "month", "yearmonth", "year_month_key"]
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue
    const normalized = normalizeYearMonth(value[key])
    if (normalized) return normalized
  }
  return ""
}

function normalizeYearMonth(value) {
  if (typeof value !== "string") return ""
  const trimmed = value.trim()
  if (!trimmed) return ""
  const hyphenMatch = /^([0-9]{4})-([0-9]{2})$/.exec(trimmed)
  if (hyphenMatch) {
    const month = Number.parseInt(hyphenMatch[2], 10)
    if (month >= 1 && month <= 12) return `${hyphenMatch[1]}-${hyphenMatch[2]}`
    return trimmed
  }
  const compactMatch = /^([0-9]{4})([0-9]{2})$/.exec(trimmed)
  if (compactMatch) {
    const month = Number.parseInt(compactMatch[2], 10)
    if (month >= 1 && month <= 12) {
      return `${compactMatch[1]}-${compactMatch[2]}`
    }
    return trimmed
  }
  return trimmed
}

function parseCountValue(value) {
  if (!value || typeof value !== "object") return null
  const keys = [
    "eventCount",
    "event_count",
    "totalCount",
    "total_count",
    "count",
    "total",
  ]
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue
    const raw = value[key]
    if (typeof raw === "number") {
      return Number.isFinite(raw) ? raw : null
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      const parsed = Number.parseFloat(raw)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return null
}

function getItemBreakdownTotals(itemBreakdown) {
  const breakdown = Array.isArray(itemBreakdown) ? itemBreakdown : []
  let ctaCount = 0
  let submissionCount = 0
  for (const entry of breakdown) {
    ctaCount += normalizeEventCountValue(entry?.asc_cta_count)
    submissionCount += normalizeEventCountValue(entry?.asc_form_submission_count)
  }
  return { ctaCount, submissionCount }
}

function extractMonthlyCounts(monthlyBreakdown, monthSet) {
  const counts = {}
  let total = 0
  const breakdown = Array.isArray(monthlyBreakdown) ? monthlyBreakdown : []
  for (const entry of breakdown) {
    const monthKey = getMonthKey(entry)
    if (!monthKey) continue
    if (monthSet) monthSet.add(monthKey)
    let monthTotal = 0
    let hasValue = false

    const events = Array.isArray(entry?.events) ? entry.events : []
    if (events.length) {
      let eventSum = 0
      let hasEventValue = false
      for (const event of events) {
        const value = parseCountValue(event)
        if (value !== null) {
          eventSum += value
          hasEventValue = true
        }
      }
      if (hasEventValue) {
        monthTotal = eventSum
        hasValue = true
      }
    }

    if (!hasValue) {
      const directValue = parseCountValue(entry)
      if (directValue !== null) {
        monthTotal = directValue
        hasValue = true
      }
    }

    if (!hasValue) continue

    counts[monthKey] = (counts[monthKey] || 0) + monthTotal
    total += monthTotal
  }
  return { counts, total }
}

function formatYearMonth(value) {
  if (typeof value !== "string" || value === "") return value || ""
  const normalized = normalizeYearMonth(value)
  const match = /^([0-9]{4})-([0-9]{2})$/.exec(normalized)
  if (!match) return normalized
  const year = Number.parseInt(match[1], 10)
  const month = Number.parseInt(match[2], 10)
  if (!Number.isFinite(year) || !Number.isFinite(month)) return normalized
  const date = new Date(Date.UTC(year, month - 1, 1))
  if (Number.isNaN(date.getTime())) return normalized
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date)
}

function renderTable(
  title,
  rows,
  selectedRowKeySet,
  toggleRowSelection,
  emptyMessage
) {
  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3 style={{ marginBottom: "0.5rem" }}>{title}</h3>
      {rows.length ? (
        <table className="ui-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "4%" }}>Select</th>
              <th>Event Owner</th>
              <th>Product Name</th>
              <th>Event Action</th>
              <th>Element Text</th>
              <th>Event Count</th>
              <th>Submissions</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rowKey}>
                <td
                  style={{ textAlign: "center", cursor: "pointer" }}
                  onClick={() => toggleRowSelection(row.rowKey)}
                >
                  <input
                    type="checkbox"
                    checked={selectedRowKeySet.has(row.rowKey)}
                    onChange={() => toggleRowSelection(row.rowKey)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select row for ${row.event_owner || "row"}`}
                  />
                </td>
                <td>{row.event_owner}</td>
                <td>{row.product_name}</td>
                <td>{row.event_action}</td>
                <td>{row.element_text || "—"}</td>
                <td>{formatNumber(row.event_count)}</td>
                <td>{formatNumber(row.submission_count)}</td>
                <td>{formatPercentValue(row.submission_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ marginTop: "0.5rem" }}>{emptyMessage}</p>
      )}
    </div>
  )
}

function renderItemFilterControl(id, label, value, options, onChange) {
  const opts = Array.isArray(options) ? options : []
  return (
    <label key={id} style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ marginBottom: "0.25rem" }}>{label}:</span>
      <select value={value} onChange={onChange}>
        <option value="">All</option>
        {opts.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  )
}

function renderItemView({ vinRows, otherRows, pageTypeRows }) {
  return (
    <>
      <p style={{ marginTop: "1rem", fontSize: "0.9em", color: "#555" }}>
        Estimated submissions are derived from the overall CTA completion rate for
        each row.
      </p>
      {renderItemRowsTable(
        "VIN-matched CTAs",
        vinRows,
        "No VIN-matched CTA interactions match the current filters."
      )}
      {renderItemRowsTable(
        "Other CTAs with VIN context",
        otherRows,
        "No CTA interactions with VIN context match the current filters."
      )}
      {renderItemPageTypeSummary(pageTypeRows)}
    </>
  )
}

function renderItemRowsTable(title, rows, emptyMessage) {
  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3 style={{ marginBottom: "0.5rem" }}>{title}</h3>
      {rows.length ? (
        <table className="ui-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Event Owner</th>
              <th>Product Name</th>
              <th>Event Action</th>
              <th>Element Text</th>
              <th>Page Type</th>
              <th>Matched VINs</th>
              <th>Event Count</th>
              <th>ASC CTA</th>
              <th>ASC Form Submission</th>
              <th>Submissions</th>
              <th>%</th>
              <th>Make</th>
              <th>Model</th>
              <th>Type</th>
              <th>Year</th>
              <th>Condition</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.event_owner}</td>
                <td>{row.product_name}</td>
                <td>{row.event_action}</td>
                <td>{row.element_text}</td>
                <td>{normalizeText(row.page_type || "")}</td>
                <td>{row.matched_vin_count ? formatNumber(row.matched_vin_count) : "—"}</td>
                <td>{formatNumber(row.event_count)}</td>
                <td>{formatNumber(row.asc_cta_count)}</td>
                <td>{formatNumber(row.asc_form_submission_count)}</td>
                <td>{formatNumber(row.submission_count)}</td>
                <td>{formatPercentValue(row.submission_rate)}</td>
                <td>{normalizeText(row.item_make || "")}</td>
                <td>{normalizeText(row.item_model || "")}</td>
                <td>{normalizeText(row.item_type || "")}</td>
                <td>{normalizeText(row.item_year || "")}</td>
                <td>{normalizeText(row.item_condition || "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ marginTop: "0.5rem" }}>{emptyMessage}</p>
      )}
    </div>
  )
}

function renderItemPageTypeSummary(rows) {
  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h3 style={{ marginBottom: "0.5rem" }}>Summary by page type</h3>
      {rows.length ? (
        <table className="ui-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Page Type</th>
              <th>Total Event Count</th>
              <th>Unique VINs</th>
              <th>Rows</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.page_type}>
                <td>{normalizeText(row.page_type || "")}</td>
                <td>{formatNumber(row.total_event_count)}</td>
                <td>{formatNumber(row.unique_vins)}</td>
                <td>{formatNumber(row.row_count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ marginTop: "0.5rem" }}>
          No additional CTA interactions with VIN context match the current filters.
        </p>
      )}
    </div>
  )
}

function sanitizeItemBreakdownEntry(entry, fallbackElementText) {
  if (!entry || typeof entry !== "object") return null
  const record = entry
  const toStringValue = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback
    if (typeof value === "string") return value
    return String(value)
  }
  const rawEventCount = record?.event_count ?? record?.eventCount ?? null
  const eventCount = normalizeEventCountValue(rawEventCount)
  const ctaCount = normalizeEventCountValue(
    record?.asc_cta_count ?? record?.ascCtaCount
  )
  const formSubmissionCount = normalizeEventCountValue(
    record?.asc_form_submission_count ?? record?.ascFormSubmissionCount
  )
  const itemId = toStringValue(record?.item_id ?? record?.itemId, "")
  const elementText = toStringValue(
    record?.element_text ?? record?.elementText,
    fallbackElementText || ""
  )
  const pageType = toStringValue(record?.page_type ?? record?.pageType, "")
  const itemMake = toStringValue(record?.item_make, "")
  const itemModel = toStringValue(record?.item_model, "")
  const itemType = toStringValue(record?.item_type, "")
  const itemYear = toStringValue(record?.item_year, "")
  const itemCondition = toStringValue(record?.item_condition, "")

  const normalizedItemId = normalizeKeyValue(itemId)
  const normalizedElement = normalizeKeyValue(elementText)
  const hasConcreteItemId =
    itemId && normalizedItemId && normalizedItemId !== "(not set)"
  const hasConcreteElement =
    elementText && normalizedElement && normalizedElement !== "(not set)"
  const explicitVinMatchValue = record?.is_vin_match ?? record?.isVinMatch
  const hasExplicitVinMatch = typeof explicitVinMatchValue === "boolean"
  const isVinMatch = hasExplicitVinMatch
    ? explicitVinMatchValue
    : isVinMatchFromItemContext(itemId, elementText, {
        hasConcreteItemId,
        hasConcreteElement,
      })

  return {
    item_id: itemId,
    element_text: elementText,
    page_type: pageType,
    item_make: itemMake,
    item_model: itemModel,
    item_type: itemType,
    item_year: itemYear,
    item_condition: itemCondition,
    event_count: eventCount,
    asc_cta_count: ctaCount,
    asc_form_submission_count: formSubmissionCount,
    isVinMatch,
  }
}

function normalizeVinToken(value) {
  if (value === null || value === undefined) return ""
  return String(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
}

function isVinMatchFromItemContext(itemId, elementText, context = {}) {
  const hasConcreteItemId = Boolean(context?.hasConcreteItemId)
  const hasConcreteElement = Boolean(context?.hasConcreteElement)
  if (!hasConcreteItemId || !hasConcreteElement) return false
  const itemToken = normalizeVinToken(itemId)
  const elementToken = normalizeVinToken(elementText)
  if (!itemToken || !elementToken) return false
  if (itemToken.length < 8) return false
  return elementToken === itemToken || elementToken.includes(itemToken)
}

function normalizeEventCountValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

function matchesItemFilters(entry, filters) {
  if (!entry) return false
  const matches = (value, filter) => {
    if (!filter) return true
    return normalizeKeyValue(value) === normalizeKeyValue(filter)
  }
  return (
    matches(entry.page_type, filters.pageType) &&
    matches(entry.item_make, filters.make) &&
    matches(entry.item_model, filters.model) &&
    matches(entry.item_type, filters.type) &&
    matches(entry.item_year, filters.year) &&
    matches(entry.item_condition, filters.condition)
  )
}
